/**
 * The `estado` badge — mirrors `content/VisibilityBadge.tsx`'s shape.
 *
 * `estado` is unconstrained text in the database (`adminLocationSchema`'s own comment explains
 * why), and the legacy dashboard's own badge only ever handled three values: `activo`,
 * `no_activo`, `en_proceso`. A fourth value reaching this component is not a defect in either
 * dashboard — it is a row an operator typed something else into — so an unrecognised value falls
 * back to a neutral badge showing the raw text rather than throwing or rendering blank.
 */
import { Badge, type BadgeProps, type BadgeShape } from '@apuestapr/ui';
import type { ReactElement } from 'react';

interface StatusPresentation {
  readonly label: string;
  readonly shape: BadgeShape;
  readonly variant: NonNullable<BadgeProps['variant']>;
}

const knownStatuses: Readonly<Record<string, StatusPresentation>> = {
  activo: { label: 'Activo', shape: 'circle', variant: 'accent' },
  no_activo: { label: 'No activo', shape: 'square', variant: 'destructive' },
  en_proceso: { label: 'En proceso', shape: 'diamond', variant: 'outline' },
  pendiente: { label: 'Pendiente', shape: 'diamond', variant: 'neutral' },
};

export const LocationStatusBadge = ({ estado }: { readonly estado: string }): ReactElement => {
  const presentation = knownStatuses[estado] ?? {
    label: estado,
    shape: 'circle',
    variant: 'neutral',
  };
  return (
    <Badge data-estado={estado} shape={presentation.shape} variant={presentation.variant}>
      {presentation.label}
    </Badge>
  );
};
